import {
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { RouterModule, RouterOutlet } from "@angular/router";
import { CommonModule } from "@angular/common";
import { MatSidenavModule } from "@angular/material/sidenav";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatIconModule } from "@angular/material/icon";
import { MatMenuModule } from "@angular/material/menu";
import { MatListModule } from "@angular/material/list";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatButtonModule } from "@angular/material/button";
import { MediaMatcher } from "@angular/cdk/layout";
import { MatCardModule } from "@angular/material/card";
import { MsalModule } from "@azure/msal-angular";
import { NgIdleModule } from "@ng-idle/core";
import { MomentModule } from "ngx-moment";
import { Store } from "@ngrx/store";
import { UserActions } from "@store/actions/user/user.actions";
import {
  selectActiveUser,
  selectHasAdminRights,
  selectIsAdmin,
  selectIsLoggedIn,
  selectIsSuperUser
} from "@store/selectors/user/user.selectors";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { AddTicketModalComponent } from "@components/modals/add-ticket-modal/add-ticket-modal.component";
import { IdleService } from "@services/idle.service";
import {
  selectIdleWarning,
  selectIdleValueAsPercent,
  selectIdleWarningText,
} from "@store/selectors/idle/idle.selectors";
import {
  selectPageLabel,
  selectCurrentIcon,
  selectVisibleMenuItems,
} from "@store/selectors/topbar/topbar.selectors";
import { ModalStateService } from "@services/modal-state.service";
import { Subject, Subscription } from "rxjs";
import { selectOrganizationLegacyId, selectOrganizationName } from "@store/selectors/add-organization/add-organization.selectors";
import { MatTooltipModule } from "@angular/material/tooltip";
import { OverlayContainer } from "@angular/cdk/overlay";
import { OrgSwitcherComponent } from "./components/org-switcher/org-switcher.component";

@Component({
  selector: "app-root",
  imports: [
    CommonModule,
    RouterOutlet,
    RouterModule,
    MatSidenavModule,
    MatToolbarModule,
    MatIconModule,
    MatMenuModule,
    MatListModule,
    MatButtonModule,
    MsalModule,
    MomentModule,
    NgIdleModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatCardModule,
    MatTooltipModule,
    MatDialogModule,
    OrgSwitcherComponent
],
  templateUrl: "./app.component.html",
  styleUrl: "./app.component.css",
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly _destroying$ = new Subject<void>();

  title = "Matrix PPA";

  menuItems$ = this.store.select(selectVisibleMenuItems);
  activeUser$ = this.store.select(selectActiveUser);
  isLoggedIn$ = this.store.select(selectIsLoggedIn);
  idleWarning$ = this.store.select(selectIdleWarning);
  idleValuePercent$ = this.store.select(selectIdleValueAsPercent);
  idleWarningText$ = this.store.select(selectIdleWarningText);
  currentPageLabel$ = this.store.select(selectPageLabel);
  currentIcon$ = this.store.select(selectCurrentIcon);
  isAdmin$ = this.store.select(selectIsAdmin);
  isSuperUser$ = this.store.select(selectIsSuperUser);
  hasAdminRights$ = this.store.select(selectHasAdminRights);
  activeOrganizationName$ = this.store.select(selectOrganizationName);
  organizationLegacyName$ = this.store.select(selectOrganizationLegacyId);
  private subs: Subscription[] = [];

  private _mobileQueryListener: () => void;
  mobileQuery: MediaQueryList;
  isModalOpen = false;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private media: MediaMatcher,
    private idleService: IdleService,
    private modalStateService: ModalStateService,
    private dialog: MatDialog,
    overlayContainer: OverlayContainer
  ) {
    this.mobileQuery = this.media.matchMedia("(max-width: 1000px)");
    this._mobileQueryListener = () => changeDetectorRef.detectChanges();
    this.mobileQuery.addListener(this._mobileQueryListener);
    overlayContainer.getContainerElement().style.zIndex = '9999';
  }

  ngOnInit() {
    this.subs.push(this.modalStateService.modalOpen$.subscribe((isOpen) => {
      this.isModalOpen = isOpen;
      this.changeDetectorRef.detectChanges();
    }));
  }

  logout(popup?: boolean) {
    this.store.dispatch(UserActions.logoutButtonPressed());
  }

  openReportIssue(): void {
    this.dialog.open(AddTicketModalComponent, {
      width: "600px",
      data: {},
    });
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub => sub?.unsubscribe));
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
